import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { supabase } from '@/lib/supabase';

/** OAuth (PKCE) landing. Supabase exchanges the code from the URL; we wait for a session, then go home. */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      // Supabase can return errors in the query or, after some provider failures, in the hash.
      const params = new URLSearchParams(location.search);
      const hash = new URLSearchParams(location.hash.replace(/^#/, ''));
      const code = params.get('code');
      const providerError = params.get('error_description') ?? params.get('error') ?? hash.get('error_description') ?? hash.get('error');
      let exchangeError: string | null = null;
      if (code) {
        const { error: err } = await supabase().auth.exchangeCodeForSession(code);
        exchangeError = err?.message ?? null;
      }
      // A failed exchange can still leave a valid session (a revisited or double-handled callback
      // URL), so the session is the verdict, not the exchange.
      const { data } = await supabase().auth.getSession();
      if (cancelled) return;
      if (data.session) await navigate({ to: '/', replace: true });
      else if (providerError) setError(`Sign-in was refused: ${providerError}`);
      else if (exchangeError) setError(exchangeError);
      else if (!code) setError('Sign-in returned without a code. Try again.');
      else setError('No session after sign-in. Try again.');
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [navigate]);
  return (
    <main className="signin">
      {error ? <div className="card stack"><div className="error" role="alert">{error}</div><a className="btn" href="/sign-in">Back to sign in</a></div> : <div className="spinner" aria-label="Signing in" />}
    </main>
  );
}
